import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Field, TextField, DateField, Btn, IModalComponent, Checkbox, FormButtonDisabler, Validations } from '@samc/centricity-core';
import { AssetClassDropdownField, BalanceTypeDropdownField, OldCountryDropdownField as CountryDropdownField, CurrencyDropdownField, TaskTemplateDropdownField } from '@/Components/Dropdowns';
import { BuyPackageModalValidator } from '@/Validators';
import { IBuyPackage, IPackageBuyInfo } from '@/Interfaces';
import { format } from 'date-fns';
import { filterPackageName } from '@/utils';

@Component({
  name: 'package-form-modal',
  components: { Field, TextField, DateField, Btn, AssetClassDropdownField, BalanceTypeDropdownField, CountryDropdownField, CurrencyDropdownField, TaskTemplateDropdownField, Checkbox, FormButtonDisabler }
})
export default class PackageFormModal extends Vue implements IModalComponent {
  @Prop() public addCloseProp!: (callback: () => Promise<any>) => void;
  @Prop() public closeProp!: () => void;
  @Prop({ required: true }) public sellerMoniker!: string;
  @Prop() public getDefaultPackageNameCallback?: (prefix: string) => Promise<string>;

  public includeWholeLoanRights: boolean = true;

  public validator = new BuyPackageModalValidator();
  public showValidations: boolean = false;
  public model: IBuyPackage = {
    status: 'Bid',
    alias: '',
    name: this.getDefaultPackagePrefix(),
    assetClass: { id: 1, name: '' },
    packageBuyInfo: {
      bidRequestDate: '',
      bidDueDate: '',
      balanceType: { id: 'A', name: '' },
      includeLoanRights: true,
      includePPPRights: true,
      includeServicingRights: true,
      channelTypeId: 'B',

      forwardId: null,
      forwardParentId: null,
      recalculateInterest: false,
      loanLevelPricingIndicator: false,

      proposedServicerId: null,
      proposedServicerTransferDate: null,
      interimServicerId: null,
      proposedCustodianId: null,
      proposedCustodianTransferDate: null,
      buyer: { id: '', name: '' },
      seller: { id: '', name: '', associatedInternalEntity: '' },
    },
    currency: { id: 'USD', name: '' },
    country: { id: 'USA', name: '' },
    packageExternalRefs: [],
    forwardId: null,
    forwardParentId: null,
    packageSubType: { id: 1, name: '', packageType: { id: 1, name: '' } },
    comments: '',
    taskTypeTemplateId: null
  };

  public get validations(): Validations<IBuyPackage> { return this.validator.isValid(this.model); }

  public get bidRequestDateValidation(): string { return this.UnwrapString(this.validations?.packageBuyInfo)?.bidRequestDate as string ?? ''; }
  public get bidDueDateValidation(): string { return this.UnwrapString(this.validations?.packageBuyInfo)?.bidDueDate as string ?? ''; }
  public get assetClassValidation(): string { return this.UnwrapString(this.validations?.assetClass)?.id ?? ''; }
  public get balanceTypeValidation(): string { return this.UnwrapString(this.UnwrapString(this.validations?.packageBuyInfo)?.balanceType)?.id ?? ''; }
  public get countryValidation(): string { return this.UnwrapString(this.UnwrapString(this.validations)?.country)?.id ?? ''; }
  public get currencyValidation(): string { return this.UnwrapString(this.validations?.currency)?.id ?? ''; }
  public get includeLoanRightsValidation(): string { return this.UnwrapString(this.validations?.packageBuyInfo)?.includeLoanRights ?? ''; }

  public UnwrapString<T>(obj?: Validations<T> | string): Validations<T> | undefined {
    return typeof obj === 'object' ? obj as Validations<T> | undefined : undefined;
  }

  private packageNameHasUserInput: boolean = false;  

  public packageNameDisabled: boolean = false;

  public mounted() {
    this.addCloseProp(async () => {
      this.showValidations = true;
      if (Object.keys(this.validations).length === 0) {
        return this.model;
      }
    });
    if (this.sellerMoniker !== '' && this.sellerMoniker !== undefined) {
      this.packageNameDisabled = true;
    }
  }

  @Watch('model.packageBuyInfo.bidDueDate')
  public async onBidDueDateChange(newValue: string | Date) {
    if (this.packageNameHasUserInput) return;
    const prefix = this.getDefaultPackagePrefix();
    this.model.name = await this.getDefaultPackageName(prefix);
  }

  @Watch('includeWholeLoanRights')
  public toggleAllStripTypeIncludes(includeWholeLoan: boolean) {
    if (includeWholeLoan) {
      this.model.packageBuyInfo.includeLoanRights = true;
      this.model.packageBuyInfo.includePPPRights = true;
      this.model.packageBuyInfo.includeServicingRights = true;
    } else {
      this.model.packageBuyInfo.includeLoanRights = false;
      this.model.packageBuyInfo.includePPPRights = false;
      this.model.packageBuyInfo.includeServicingRights = false;
    }
  }

  public onPackageNameInput(): void {
    this.packageNameHasUserInput = true;
    filterPackageName(
      this.model.name,
      (filteredValue) => this.model.name = filteredValue,
      () => this.$alert?.warning('Special characters are not allowed in package names')
    );
  }

  private getDefaultPackagePrefix(): string {
    if (this.sellerMoniker === '' || this.sellerMoniker === undefined) return '';

    const bidDate = this.model?.packageBuyInfo?.bidDueDate;
    if (bidDate === '' || bidDate === undefined) {
      return this.sellerMoniker;
    }else {
      return `${this.sellerMoniker}_${format(new Date(bidDate), 'yyyyMMdd') }`;
    }
  }

  private async getDefaultPackageName(prefix: string): Promise<string> {
    if (prefix === '' || this.getDefaultPackageNameCallback === undefined) return prefix;
    this.packageNameDisabled = true;

    try {
      this.$loading('Generating default package name.');
      const res = await this.getDefaultPackageNameCallback(prefix);
      if (res !== '') {
        this.packageNameDisabled = false;
        return res;
      }
      this.packageNameDisabled = true;
      this.$alert.warning('Failed to generate default package name.', `Packages with names from '${prefix}A' to '${prefix}Z' already exist.`,false)
      return prefix;
    }
    finally {
      this.$loading('');
    }
  }  
}
